/**
 * Care Package, copied link: hand the link to the sender.
 *
 * Messaging is not wired, so the sender sends it from their own phone to the
 * one person it is for (a gift is one-to-one, recipient journey R4.33). On a
 * phone the share sheet is the shortest road to Messages; on a computer (or
 * when the sheet is refused or missing) the clipboard is, and the clipboard
 * holds the link alone.
 */
export function prefersShareSheet(win = (typeof window !== 'undefined' ? window : null)) {
  if (!win || !win.navigator || typeof win.navigator.share !== 'function') return false
  try { return !!(win.matchMedia && win.matchMedia('(pointer: coarse)').matches) } catch (e) { return false }
}

export async function copyText(text, win = (typeof window !== 'undefined' ? window : null), doc = (typeof document !== 'undefined' ? document : null)) {
  try {
    if (win && win.navigator && win.navigator.clipboard && win.navigator.clipboard.writeText) {
      await win.navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) { /* fall through to the old way */ }
  if (!doc || !doc.body) return false
  const ta = doc.createElement('textarea')
  ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'
  doc.body.appendChild(ta); ta.select()
  let ok = false
  try { ok = !!doc.execCommand('copy') } catch (e) { ok = false }
  doc.body.removeChild(ta)
  return ok
}

/**
 * The words that ride with the link in a share sheet: the message without the
 * link itself (the sheet carries that as its url, and most apps would print it
 * twice).
 */
export function shareText(message, link) {
  const m = message || ''
  if (!link || !m.includes(link)) return m.trim()
  return m.split(link).join('').trim()
}

/**
 * Hand over a copied link. The clipboard gets the LINK ALONE: whatever is
 * pasted into an address bar has to be an address (the whole message there is
 * a search). A share sheet gets the words as text and the link as url.
 * pkg: { link, message }. Returns 'shared' | 'copied' | 'cancelled' | 'failed'.
 */
export async function deliverLink(pkg, { share = false, win, doc } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  const link = (pkg && pkg.link) || ''
  if (share && w && w.navigator && typeof w.navigator.share === 'function') {
    try { await w.navigator.share({ text: shareText(pkg && pkg.message, link), url: link }); return 'shared' }
    catch (e) { if (e && e.name === 'AbortError') return 'cancelled' }
  }
  return (await copyText(link, w, doc === undefined ? (typeof document !== 'undefined' ? document : null) : doc)) ? 'copied' : 'failed'
}

// ── The hand-over inside the tap (2026-10-19) ────────────────────────────────
//
// iPhone Safari only opens the share sheet (and only lets a page write the
// clipboard) while the tap that asked for it is still "live". Awaiting a
// request first (the link used to be made on the tap, with model calls in it)
// spends that, so the sheet never opened and the copy failed too. The link is
// now made when the send screen opens, and handOver calls navigator.share or
// navigator.clipboard.writeText SYNCHRONOUSLY, before its first await.

export const GIFT_TEXT_MAX = 320

/**
 * The words that ride with the link in a share sheet, built the way the
 * server builds the pasted message (first line, the sender's note, the link
 * last, at most 320 characters with the link), without the link itself.
 */
export function giftShareText(firstLine, note, link, max = GIFT_TEXT_MAX) {
  const first = (firstLine || '').trim()
  let n = (note || '').trim()
  const room = max - first.length - (link ? link.length + 2 : 0)
  if (n && room > 8) {
    if (n.length + 2 > room) n = n.slice(0, room - 3).trimEnd() + '…'
    return `${first}\n\n${n}`
  }
  return first
}

/**
 * An sms: link with the body filled in. "?&body=" opens with the body on both
 * iPhone and Android.
 */
export function smsHref(number, body) {
  return `sms:${number}?&body=${encodeURIComponent(body || '')}`
}

function copyNow(text, w, d) {
  // Called inside the gesture: writeText starts before anything awaits.
  let started = null
  try {
    if (w && w.navigator && w.navigator.clipboard && typeof w.navigator.clipboard.writeText === 'function') {
      started = w.navigator.clipboard.writeText(text)
    }
  } catch (e) { started = null }
  const legacy = () => {
    if (!d || !d.body) return false
    const ta = d.createElement('textarea')
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'
    d.body.appendChild(ta); ta.select()
    let ok = false
    try { ok = !!d.execCommand('copy') } catch (e) { ok = false }
    d.body.removeChild(ta)
    return ok
  }
  if (!started) return Promise.resolve(legacy() ? 'copied' : 'failed')
  return Promise.resolve(started).then(() => 'copied', () => (legacy() ? 'copied' : 'failed'))
}

/**
 * Share or copy a link that is already made, from inside a tap.
 * pkg: { link, text, title }. share: open the share sheet (a phone).
 * sms: a number in E.164; opens the sender's Messages to it instead.
 * Resolves 'shared' | 'copied' | 'cancelled' | 'failed'. A cancelled sheet is
 * 'cancelled' (stay put, say nothing); a sheet that refuses falls back to
 * copying; 'failed' means neither worked (show the link to copy by hand).
 */
export function handOver(pkg, { share = false, sms = '', win, doc } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  const d = doc === undefined ? (typeof document !== 'undefined' ? document : null) : doc
  const link = (pkg && pkg.link) || ''
  // A typed phone (texting, 2026-10-31): we never text a number someone else
  // typed, so the sender's own Messages opens to it, the words and the link
  // already in. Inside the tap, like the sheet.
  if (sms && w && w.location) {
    try {
      w.location.href = smsHref(sms, [pkg && pkg.text, link].filter(Boolean).join('\n\n'))
      return Promise.resolve('shared')
    } catch (e) { /* no Messages here: the sheet or the clipboard below */ }
  }
  if (share && w && w.navigator && typeof w.navigator.share === 'function') {
    let started
    try {
      const data = { url: link }
      if (pkg && pkg.text) data.text = pkg.text
      if (pkg && pkg.title) data.title = pkg.title
      started = w.navigator.share(data)
    } catch (e) { started = Promise.reject(e) }
    return Promise.resolve(started).then(() => 'shared', (e) => {
      if (e && e.name === 'AbortError') return 'cancelled'
      return copyNow(link, w, d)
    })
  }
  return copyNow(link, w, d)
}
